import { Controller } from "@hotwired/stimulus"
import { prepareUnclosedMixTagOpeners, restoreUnclosedMixTagOpeners } from '../helpers/tagify_unclosed_mix_openers.js'

export default class extends Controller {
  static values = {
    inputId: String,
    originalValue: String
  }

  getOriginalValueFromResetButton(resetSection) {
    const resetButton = resetSection.querySelector('[data-field-reset-original-value-value]')
    if (resetButton) {
      return resetButton.dataset.fieldResetOriginalValueValue
    }
    return null
  }

  restore(event) {
    let input = document.getElementById(this.inputIdValue)
    const original = this.originalValueValue

    if (!input) {
      const nameAttribute = this.convertInputIdToName(this.inputIdValue)

      // Try to find radio group
      const radios = document.querySelectorAll(`input[type="radio"][name="${nameAttribute}"]`)
      if (radios.length > 0) {
        this.updateRadioButtons(radios, original)
        this.removeResetSection(radios[0])
        return
      }

      // Try to find MultiCheckboxDropdownComponent
      const multiCheckboxInputs = document.querySelectorAll(`input[name="${nameAttribute}[]"]`)
      if (multiCheckboxInputs.length > 0) {
        this.updateMultiCheckboxDropdown(multiCheckboxInputs, original)
        this.removeResetSection(multiCheckboxInputs[0])
        return
      }

      // Fallback to other inputs
      const otherInputs = document.querySelectorAll(`input[name="${nameAttribute}"], select[name="${nameAttribute}"]`)
      if (otherInputs.length > 0) {
        // For checkboxes, prefer the visible checkbox over the hidden input
        const checkbox = Array.from(otherInputs).find(input => input.type === 'checkbox')
        input = checkbox || otherInputs[0]
      } else {
        return
      }
    }

    const type = input.type

    if (input.dataset.tablesTarget === "input") {
      this.updateDatatableField(input, original)
      return
    }

    if (input.type === "hidden") {
      this.updateHiddenInput(input, original)
      this.updateTagifyUI(input, original)
      return
    }

    if (type === "checkbox") {
      // Normalize to strings for comparison since input.value is always a string
      input.checked = (String(original) === input.value)
    }

    else if (type === "radio") {
      // Find all radio buttons with the same name
      const nameAttribute = input.name
      const allRadios = document.querySelectorAll(`input[name="${nameAttribute}"]`)
      this.updateRadioButtons(allRadios, original)
    }

    else if (type === "select-one" || type === "select-multiple") {
      this.setSelectValue(input, original)
    }

    else {
      input.value = original
    }

    this.triggerChange(input)
    this.removeResetSection(input)
  }

  convertInputIdToName(inputId) {
    const match = inputId.match(/^(.*?)_([a-zA-Z0-9_]+)$/)
    if (!match) {
      return inputId
    }

    const prefix = match[1]
    const fieldName = match[2]

    const result = `${prefix}[${fieldName}]`

    return result
  }

  updateDatatableField(input, original) {
    input.value = original
    this.triggerChange(input)

    const tablesController = input.closest('[data-controller="tables"]')

    if (tablesController) {
      // Get the tables controller instance
      const tablesControllerInstance = this.application.getControllerForElementAndIdentifier(tablesController, 'tables')

      if (tablesControllerInstance) {
        // Destroy the existing table completely
        if (tablesControllerInstance.table) {
          tablesControllerInstance.table.destroy()
          tablesControllerInstance.table = null
        }

        // Clear the container
        const container = tablesController.querySelector('.tabulator')
        if (container) {
          container.remove()
        }

        // Reinitialize the table with new data
        setTimeout(() => {
          tablesControllerInstance.connect()
        }, 10)
      }
    }

    this.removeResetSection(input)
  }

  updateRadioButtons(radios, original) {
    // Normalize to string for comparison since radio.value is always a string
    const normalizedOriginal = String(original)

    radios.forEach(radio => {
      const wasChecked = radio.checked
      radio.checked = radio.value === normalizedOriginal

      this.triggerChange(radio)
    })
  }

  removeResetSection(input) {
    const inputId = input.id
    const inputName = input.name
    const nameAsId = inputName.replace(/\[|\]/g, '_').replace(/_+$/, '')

    // For MultiCheckboxDropdownComponent, remove the [] suffix from name
    const baseName = inputName.replace(/\[\]$/, '')

    const allResetSections = document.querySelectorAll('.field-reset-section')
    const allChangedIndicators = document.querySelectorAll('.field-changed-indicator')

    // Match input to data-field-reset-input-id-value
    const matchesInput = (el) => {
      const dataId = el.dataset.fieldResetInputIdValue
      return (
        dataId === inputId ||
        dataId === inputName ||
        dataId === nameAsId ||
        dataId === baseName
      )
    }

    // Find the right reset-section
    const matchingResetSection = Array.from(allResetSections).find(matchesInput)
    if (matchingResetSection) {
      matchingResetSection.style.display = 'none'
    } else {
      console.warn('No matching reset section found')
    }

    // Find the right changed-indicator
    const matchingChangedIndicator = Array.from(allChangedIndicators).find(matchesInput)
    if (matchingChangedIndicator) {
      matchingChangedIndicator.style.display = 'none'
    } else {
      console.warn('No matching changed indicator found')
    }
  }

  updateHiddenInput(input, original) {
    input.value = original
    this.triggerChange(input)
  }

  updateTagifyUI(input, original) {
    const wrapper = input.closest(".tagify-parent-wrapper")
    if (!wrapper) return;

    // Verify that input is actually in this wrapper
    if (input.closest(".tagify-parent-wrapper") !== wrapper) {
      console.warn('updateTagifyUI: Input is not in the expected wrapper');
      return;
    }

    // Find tagify input element within the same wrapper
    // Use double quotes for proper escaping of the name attribute
    const tagifyInput = wrapper.querySelector(`input.tagify[data-target-name="${input.name}"]`)

    if (tagifyInput) {
      // Verify that tagifyInput is in the same wrapper
      if (tagifyInput.closest(".tagify-parent-wrapper") !== wrapper) {
        console.warn('updateTagifyUI: Found tagifyInput but it\'s not in the same wrapper!');
        return;
      }

      let tagifyInstance = null

      // Check different ways Tagify can be stored
      if (tagifyInput.__tagify) {
        tagifyInstance = tagifyInput.__tagify
      } else {
        // If tagify instance is not found, try to find it by matching target name
        // This should not happen if tagify is properly initialized, but as a fallback
        const allTagifyInputs = wrapper.querySelectorAll('input.tagify')
        for (const element of allTagifyInputs) {
          if (element.__tagify && element.dataset.targetName === input.name &&
              element.closest(".tagify-parent-wrapper") === wrapper) {
            tagifyInstance = element.__tagify
            break
          }
        }
      }

      if (tagifyInstance) {
        const preparedValue = prepareUnclosedMixTagOpeners(original)

        // Use Tagify's loadOriginalValues method to properly restore the value
        tagifyInstance.loadOriginalValues(preparedValue.value)
        restoreUnclosedMixTagOpeners(tagifyInstance, preparedValue.token)

        // Update hidden input with the current value
        const eventElement = tagifyInstance.DOM.input
        input.value = this.buildInputValue(eventElement)
        this.triggerChange(input)

        this.removeResetSection(input)

        const form = input.closest('form')
        if (form && typeof form.requestSubmit === 'function') {
          form.requestSubmit()
        }
      } else {
        input.value = original
        this.triggerChange(input)

        this.removeResetSection(input)

        const form = input.closest('form')
        if (form && typeof form.requestSubmit === 'function') {
          form.requestSubmit()
        }
      }
    } else {
    }
  }

  parseTagifyValue(original) {
    if (typeof original !== 'string') {
      return []
    }

    // Handle Tagify's mix-mode format: "{{ uuid1.freetext1 }} {{ uuid2.freetext2 }}"
    const tagPattern = /\{\{\s*([^}]+)\s*\}\}/g
    const matches = [...original.matchAll(tagPattern)]

    if (matches.length > 0) {
      const result = matches.map(match => {
        const content = match[1].trim()
        // Use the same logic as extractUuidAndFreetext to properly handle liquid filters
        const [uuid, freeText] = this.extractUuidAndFreetext(content)
        const tag = {
          value: uuid,
          name: uuid,
          freeText: freeText
        }
        return tag
      })
      return result
    }

    // If it's not in Tagify-format, handle as normal text
    if (original.trim()) {
      const tag = {
        value: original.trim(),
        name: original.trim(),
        freeText: ''
      }
      return [tag]
    }

    return []
  }

  // Copy buildInputValue function from customized_tagify.js
  buildInputValue(eventElement) {
    let inputValue = ''
    const inputElements = [].slice.call(eventElement.childNodes)

    inputElements.forEach(function(element) {
      if (element.hasOwnProperty('__tagifyTagData')) {
        if (element.__tagifyTagData.value === undefined) return
        if (element.classList.contains('tagify__tag--hide')) return

        const freeText = element.querySelector('.inner-input').value
        inputValue += `{{ ${element.__tagifyTagData.value}${freeText} }}`
      } else if (element.textContent !== undefined) {
        inputValue += element.textContent
      }
    })

    return this.clearValue(inputValue)
  }

  clearValue(input) {
    return input.replace(/[\u200B-\u200D\uFEFF]/g, '').replace(/[\s\n\t]+$/g, '')
  }

  setSelectValue(select, original) {
    if (select.multiple && Array.isArray(original)) {
      Array.from(select.options).forEach(option => {
        option.selected = original.includes(option.value)
      })
    } else {
      select.value = original
    }
  }

  triggerChange(input) {
    // Prevent debounced auto-submit when reset action
    input.dataset.fieldReset = 'true'

    input.dispatchEvent(new Event("input", { bubbles: true }))
    input.dispatchEvent(new Event("change", { bubbles: true }))

    // Trigger blur event to activate auto-submit for fields that listen to blur
    input.dispatchEvent(new Event("blur", { bubbles: true }))

    input.classList.add("restored")
    setTimeout(() => {
      input.classList.remove("restored")
      delete input.dataset.fieldReset
    }, 1000)
  }

  updateMultiCheckboxDropdown(inputs, original) {
    let originalValues = []

    try {
      if (original.startsWith('[') && original.endsWith(']')) {
        originalValues = JSON.parse(original)
      } else if (typeof original === 'string') {
        originalValues = original.split(',').map(v => v.trim()).filter(v => v)
      } else if (Array.isArray(original)) {
        originalValues = original
      }
    } catch (e) {
      console.warn('Failed to parse original value:', original)
      return
    }

    // Normalize to strings for comparison since input.value is always a string
    const normalizedOriginalValues = originalValues.map(v => String(v))

    inputs.forEach(input => {
      if (input.type === 'checkbox') {
        input.checked = normalizedOriginalValues.includes(input.value)
        this.triggerChange(input)
      }
    })

    const wrapper = inputs[0].closest('[data-controller="multi-checkbox-dropdown-component"]')
    if (wrapper) {
      const controller = this.application.getControllerForElementAndIdentifier(wrapper, 'multi-checkbox-dropdown-component')
      if (controller && controller.updateSelectedLabel) {
        controller.updateSelectedLabel()
      }
    }

    this.removeResetSection(inputs[0])
  }
}
